type TimeAheadUnit =
    | 'YEAR'
    | 'MONTH'
    | 'DAY'
    | 'WORKING_DAY'
    | 'HOUR'
    | 'MINUTE';

type TimeAhead = {
    value?: number;
    type: TimeAheadUnit;
};

export type LeadTimeMode = 'SECONDARY_ASSIGNMENT' | 'SECONDARY_TRANSFER_OF_USE';

type LeadTimeRule = {
    id: number;
    name: string;
    mode: LeadTimeMode;
    contractPeriodType: 'NON_WITHINDAY';
    contractPeriod?: number;
    leadTimePeriodForMaximumTimeAhead: TimeAhead;
    leadTimePeriodForMinimumTimeBefore: TimeAhead;
};

type Runtime = {
    start: Date;
    end: Date;
};

type ExpirationDateHint = {
    hint: string;
    minDate?: Date;
    maxDate?: Date;
};

const dateFormat = 'DD.MM.YYYY HH:mm';

export function leadTimeExpirationDateHint({
    runtime,
    rules
}: {
    runtime?: Runtime;
    rules: Array<{ rule: LeadTimeRule; tso: string }>;
}): ExpirationDateHint {
    if (!runtime || !rules || rules.length === 0) return;

    const minExpirationDate = rules.reduce((min, { rule }) => {
        const date = calculateOffsetDate(
            runtime.start,
            rule.leadTimePeriodForMaximumTimeAhead.value,
            rule.leadTimePeriodForMaximumTimeAhead.type
        );
        if (!min || min.isBefore(date)) return date;
        return min;
    }, undefined as moment.Moment);

    const maxExpirationDate = rules.reduce((max, { rule }) => {
        const date = calculateOffsetDate(
            runtime.start,
            rule.leadTimePeriodForMinimumTimeBefore.value,
            rule.leadTimePeriodForMinimumTimeBefore.type
        );
        if (!max || max.isAfter(date)) return date;
        return max;
    }, undefined as moment.Moment);

    const tso = rules.map((r) => r.tso).join(' and ');

    if (!minExpirationDate.isBefore(maxExpirationDate))
        return {
            hint: `Lead Times defined by ${tso} do not overlap. Therefore your cannot define an Expiration Date. Please contact the TSOs.`
        };

    return {
        hint: `Lead Time defined by ${tso} is ${minExpirationDate.format(
            dateFormat
        )} - ${maxExpirationDate.format(
            dateFormat
        )}. Your Expiration Date has to be within this period.`,
        minDate: minExpirationDate.startOf('day').toDate(),
        maxDate: minExpirationDate.startOf('day').add(1, 'day').toDate()
    };
}

export function findLeadTimeRule({
    runtime,
    mode,
    leadTimeRules
}: {
    runtime?: Runtime;
    mode?: LeadTimeMode;
    leadTimeRules?: LeadTimeRule[];
}) {
    if (!runtime || !mode || !leadTimeRules || leadTimeRules.length === 0)
        return;
    const runTimeMonths = runtimeMonths(runtime);
    const preparedLeadTimeRules = leadTimeRules
        .filter(
            (r) =>
                r.leadTimePeriodForMinimumTimeBefore.value != null &&
                r.leadTimePeriodForMaximumTimeAhead.value != null
        )
        .map((r) => ({
            ...r,
            contractPeriod:
                r.contractPeriod == null
                    ? Number.MAX_SAFE_INTEGER
                    : r.contractPeriod
        }))
        .sort((a, b) => a.contractPeriod - b.contractPeriod);
    return preparedLeadTimeRules.find(
        (r) => r.mode === mode && runTimeMonths < r.contractPeriod
    );
}

function runtimeMonths(runtime: Runtime) {
    if (!runtime) return;
    return moment(runtime.end).diff(moment(runtime.start), 'months');
}

function calculateOffsetDate(
    reference: Date,
    offset: number,
    unit: TimeAheadUnit
) {
    const momentUnit = momentUnits[unit];
    if (typeof momentUnit === 'function') return momentUnit(reference, offset);
    return moment(reference).subtract(offset, momentUnit);
}

const momentUnits: Record<
    TimeAheadUnit,
    string | ((date: Date, offset: number) => moment.Moment)
> = {
    YEAR: 'years',
    MONTH: 'months',
    DAY: 'days',
    WORKING_DAY: (date, offset) => {
        const result = moment(date);
        while (offset > 0) {
            result.subtract(1, 'days');
            if (result.day() !== 0 && result.day() !== 6) offset -= 1;
        }
        return result;
    },
    HOUR: 'hours',
    MINUTE: 'minutes'
};
